import apiClient from '../lib/api/client';
import {
  AuthResponse,
  PaginatedResponse,
  RegisterPayload,
  LoginPayload,
  UpdateUserPayload,
  ChangePasswordPayload,
  UpdatePreferencesPayload,
  AddToWatchlistPayload,
  SaveWatchProgressPayload,
  CreateRatingPayload,
  PaginationParams,
} from '../types/api';
import {
  User,
  Category,
  Series,
  SeriesDetail,
  Episode,
  WatchHistory,
  WatchlistItem,
  Rating,
  DashboardMetrics,
  SearchParams,
} from '../types/domain';

export const dramatstraAPI = {
  // Auth
  auth: {
    register: (data: RegisterPayload) =>
      apiClient.post<AuthResponse>('/accounts', data),
    login: (data: LoginPayload) =>
      apiClient.post<AuthResponse>('/sessions/', data),
    logout: () => apiClient.post('/sessions/logout'),
    refreshToken: () => apiClient.post<AuthResponse>('/sessions/refresh-token'),
    resetPassword: (email: string) =>
      apiClient.post('/password-reset-tokens', { email }),
  },

  // Users
  users: {
    getProfile: () => apiClient.get<User>('/users/me'),
    updateProfile: (data: UpdateUserPayload) =>
      apiClient.patch<User>('/users/me', data),
    changePassword: (data: ChangePasswordPayload) =>
      apiClient.post('/users/me/change-password', data),
    getPreferences: () =>
      apiClient.get<User>('/users/me'),
    updatePreferences: (prefs: UpdatePreferencesPayload) =>
      apiClient.patch<User>('/users/me', { preferences: prefs }),
  },

  // Categories
  categories: {
    list: () => apiClient.get<Category[]>('/streaming/categories'),
  },

  // Series
  series: {
    list: (params?: SearchParams) =>
      apiClient.get<PaginatedResponse<Series>>('/streaming/series', { params }),
    search: (query: string, params?: SearchParams) =>
      apiClient.get<PaginatedResponse<Series>>('/streaming/series/search', {
        params: { q: query, ...params },
      }),
    detail: (id: string) =>
      apiClient.get<SeriesDetail>(`/streaming/series/${id}`),
  },

  // Episodes
  episodes: {
    list: (seriesId: string, params?: PaginationParams) =>
      apiClient.get<Episode[]>(
        `/streaming/series/${seriesId}/episodes`,
        { params }
      ),
    detail: (seriesId: string, episodeNumber: number) =>
      apiClient.get<Episode>(
        `/streaming/series/${seriesId}/episodes/${episodeNumber}`
      ),
  },

  // Watchlist
  watchlist: {
    list: (params?: PaginationParams) =>
      apiClient.get<PaginatedResponse<WatchlistItem>>('/streaming/watchlist', {
        params,
      }),
    add: (data: AddToWatchlistPayload) =>
      apiClient.post('/streaming/watchlist', data),
    remove: (seriesId: string) =>
      apiClient.delete(`/streaming/watchlist/${seriesId}`),
  },

  // Watch History
  watchHistory: {
    list: (params?: PaginationParams) =>
      apiClient.get<PaginatedResponse<WatchHistory>>(
        '/streaming/watch-history',
        { params }
      ),
    save: (data: SaveWatchProgressPayload) =>
      apiClient.post('/streaming/watch-history', data),
    getProgress: (episodeId: string) =>
      apiClient.get<WatchHistory>(`/streaming/watch-history/${episodeId}`),
  },

  // Ratings
  ratings: {
    list: (seriesId: string) =>
      apiClient.get<Rating[]>(`/streaming/series/${seriesId}/ratings`),
    create: (data: CreateRatingPayload) =>
      apiClient.post('/streaming/ratings', data),
  },

  // Admin
  admin: {
    dashboard: () =>
      apiClient.get<DashboardMetrics>('/admin/dashboard'),
    users: {
      list: (params?: PaginationParams) =>
        apiClient.get<PaginatedResponse<User>>('/admin/users', { params }),
      detail: (id: string) =>
        apiClient.get<User>(`/admin/users/${id}`),
      update: (id: string, data: Partial<User>) =>
        apiClient.patch<User>(`/admin/users/${id}`, data),
      delete: (id: string) =>
        apiClient.delete(`/admin/users/${id}`),
      toggleStatus: (id: string) =>
        apiClient.post(`/admin/users/${id}/toggle-status`),
    },
    organizations: {
      list: (params?: PaginationParams) =>
        apiClient.get('/admin/organizations', { params }),
    },
    analytics: {
      revenue: (period?: string) =>
        apiClient.get('/admin/analytics/revenue', { params: { period } }),
    },
  },
};

export default dramatstraAPI;
